import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";

import {
	faFile,
	faWebhook,
	faSatelliteDish,
	faUserGroup,
} from "@fortawesome/pro-regular-svg-icons";

import { FunctionComponent, ReactNode } from "react";

import { ListSourceType } from "@/enum/list.enum";
import { Icon } from "@/components";
import { SvgIcon } from "@/components/Icon/SvgIcon";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import { classNames } from "@/utils/classNames";

import AppLogo from "@/assets/images/favicon.png";

import { List } from "../schema/list";
import { CatalogIconVariant } from "../pages/ListCreate/config/tileCatalog";
import {
	getSourceFilterEntry,
	getSourceIconClasses,
	SourceFilterEntry,
} from "../config/sourceTypeFilterCatalog";

export interface ListTypeElementProps {
	list: List;
	withoutName?: boolean;
}

interface ListTypeMeta {
	icon: ReactNode;
	label: string;
	/** Background/text colour classes for the icon chip (shared with the source-filter palette). */
	variantClass: string;
}

const ICON_CLASS = "h-[14px] w-[14px]";

/** Render a source-catalog entry's glyph exactly as the "Filter by source" palette does. */
const renderCatalogIcon = (entry: SourceFilterEntry): ReactNode => {
	if (entry.icon) {
		return <SvgIcon className={ICON_CLASS} svgIcon={entry.icon} />;
	}

	if (entry.secondIcon) {
		return <Icon className={ICON_CLASS} icon={entry.secondIcon} />;
	}

	return null;
};

/**
 * Resolves the icon chip + label for a list's type, sourced from the same catalog the "Filter by
 * source" palette uses so the two surfaces stay visually in sync. A few types have no catalog tile
 * (Compose / Empty / legacy profile lists) or need a sub-variant (CSV webhook), handled explicitly.
 */
const getListTypeMeta = (list: List, t: TFunction): ListTypeMeta | null => {
	if (list?.specialType !== null) {
		return {
			icon: <img alt="Compose" className="h-[16px] w-[16px]" src={AppLogo} />,
			label: "Compose",
			variantClass: getSourceIconClasses(CatalogIconVariant.EMPTY),
		};
	}

	if (list?.sourceType === ListSourceType.EMPTY) {
		return {
			icon: <Icon className={ICON_CLASS} icon={faFile} />,
			label: "Empty",
			variantClass: getSourceIconClasses(CatalogIconVariant.EMPTY),
		};
	}

	if (list?.sourceType === ListSourceType.LI_PROFILE) {
		return {
			icon: <Icon className={ICON_CLASS} icon={faSatelliteDish} />,
			label: "Custom Signal Tracker",
			variantClass: getSourceIconClasses(CatalogIconVariant.TRACKER),
		};
	}

	// LI_CONNECTIONS lists are auto-populated from synced LinkedIn connections, so they have no
	// Create-flow tile / source-filter entry — give them a LinkedIn-coloured connections glyph.
	if (list?.sourceType === ListSourceType.LI_CONNECTIONS) {
		return {
			icon: <Icon className={ICON_CLASS} icon={faUserGroup} />,
			label: t("list.LI_CONNECTIONS"),
			variantClass: getSourceIconClasses(CatalogIconVariant.LINKEDIN),
		};
	}

	const entry = getSourceFilterEntry(list?.sourceType ?? "");

	if (!entry) {
		return null;
	}

	const isWebhook =
		list?.sourceType === ListSourceType.CSV_ENRICHMENTS && !!list.csvEnrichments?.[0]?.webhook;

	return {
		icon: isWebhook ? <Icon className={ICON_CLASS} icon={faWebhook} /> : renderCatalogIcon(entry),
		label: isWebhook ? "Webhook" : t(entry.labelKey),
		variantClass: getSourceIconClasses(entry.iconVariant),
	};
};

export const ListTypeElement: FunctionComponent<ListTypeElementProps> = ({ list, withoutName }) => {
	const { t } = useTranslation();

	const meta = getListTypeMeta(list, t);

	if (!meta) {
		return <div className="flex" />;
	}

	return (
		<div className="flex items-center">
			<Tooltip content={meta.label}>
				<span
					className={classNames(
						"mr-1 flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-md",
						meta.variantClass
					)}
				>
					{meta.icon}
				</span>
			</Tooltip>
			{!withoutName && <span>{meta.label}</span>}
		</div>
	);
};
